"use client"

import AccordionComponent from "components/ui/Accordion"
import Tag from "components/ui/Tag"
import gsap, { ScrollTrigger, SplitText } from "gsap/all"
import SetLSVG from "images/Icons/SetL.inline.svg"
import woman from "images/home/value/woman.png"
import { InfiniteSideScroll } from "library/InfiniteSideScroll"
import StaticImage from "library/StaticImage"
import { css, fmobile, fresponsive, ftablet, styled } from "library/styled"
import { useAnimation } from "library/useAnimation"
import { useRef } from "react"
import colors from "styles/colors"
import textStyles from "styles/text"

gsap.registerPlugin(SplitText, ScrollTrigger)

const BEFORE_TAGS = [
	"New Network Yearly",
	"Denied Claims",
	"Change in Plans",
	"Out of Network Billing",
	"Authorization Denied",
	"Unpredicatable Pricing",
]

const AFTER_TAGS = [
	"Same Network Yearly",
	"Approved Claims",
	"Consistent Plans",
	"Out of Network Billing",
	"Authorization Accepted",
	"Predictable Pricing",
]

export default function Values() {
	const topSection = useRef<HTMLDivElement>(null)
	const carouselTitle = useRef<HTMLHeadingElement>(null)
	const beforeArlo = useRef<HTMLSpanElement>(null)
	const afterArlo = useRef<HTMLSpanElement>(null)

	useAnimation(
		() => {
			if (!topSection.current) return

			const tl = gsap.timeline({
				scrollTrigger: {
					trigger: topSection.current,
					start: "top 70%",
				},
			})

			tl.to(".home-values-woman", {
				y: 0,
				duration: 0.8,
				ease: "power2.out",
			})

			const tl2 = gsap.timeline({
				scrollTrigger: {
					trigger: topSection.current,
					start: "top 70%",
					toggleActions: "play pause resume pause",
					markers: false,
				},
				repeat: -1,
			})
			const beforeArloText = new SplitText(beforeArlo.current, {
				type: "chars",
				charsClass: "before-arlo-chars",
			})

			const afterArloText = new SplitText(afterArlo.current, {
				type: "chars",
				charsClass: "after-arlo-chars",
			})

			const midpoint = 5
			const nextStep = midpoint + beforeArloText.chars.length * 0.03
			const endpoint = midpoint * 2
			const lastStep = endpoint + afterArloText.chars.length * 0.03

			gsap.set(".after-arlo-carousel", {
				autoAlpha: 0,
			})

			tl2
				.to(
					beforeArloText.chars,
					{
						opacity: 0,
						ease: "power2.out",
						stagger: {
							each: 0.03,
							from: "end",
						},
						duration: 0,
					},
					midpoint,
				)

				.to(
					afterArloText.chars,
					{
						opacity: 1,
						color: colors.js.green400,
						ease: "power2.in",
						stagger: {
							each: 0.03,
							from: "start",
						},
						duration: 0,
					},
					nextStep,
				)
				.to(
					".after-arlo-carousel",
					{
						autoAlpha: 1,
						duration: 0.3,
						ease: "power2.in",
					},
					nextStep,
				)
				.to(
					".before-arlo-carousel",
					{
						autoAlpha: 0,
						duration: 0.3,
						ease: "power2.in",
					},
					nextStep,
				)
				.to(
					afterArloText.chars,
					{
						color: colors.js.offWhite,
						ease: "power2.out",
						stagger: {
							each: 0.03,
							from: "end",
						},
						duration: 0,
					},
					endpoint,
				)
				.to(
					".after-arlo-carousel",
					{
						autoAlpha: 0,
						duration: 0.3,
						ease: "power2.in",
					},
					lastStep,
				)
				.to(
					".before-arlo-carousel",
					{
						autoAlpha: 1,
						duration: 0.3,
						ease: "power2.in",
					},
					lastStep,
				)
				.to(
					beforeArloText.chars,
					{
						opacity: 1,
						ease: "power2.in",
						stagger: {
							each: 0.03,
							from: "start",
						},
						duration: 0,
					},
					lastStep,
				)
		},
		[],
		{ scope: topSection },
	)

	return (
		<Wrapper>
			<TopSection ref={topSection}>
				<WindowSection>
					<Window>
						<TopLayer />
						<BottomLayer />
						<Woman
							src={woman}
							alt="woman in orange tank top with hands on hips"
							width={380}
							height={457}
							className="home-values-woman"
						/>
						<CarouselWrapper>
							<LeftBlur />
							<RightBlur />

							<CarouselTitle ref={carouselTitle}>
								<Before ref={beforeArlo}>Before Arlo: </Before>
								<AfterArlo ref={afterArlo}>After Arlo:</AfterArlo>
							</CarouselTitle>
							<Carousel className="before-arlo-carousel" marqueeSpeed={0.2}>
								{BEFORE_TAGS.map((tag) => (
									<Tag key={tag}>{tag}</Tag>
								))}
							</Carousel>
							<Carousel className="after-arlo-carousel" marqueeSpeed={0.2}>
								{AFTER_TAGS.map((tag) => (
									<Tag key={tag} variant="after">
										{tag}
									</Tag>
								))}
							</Carousel>
						</CarouselWrapper>
					</Window>
				</WindowSection>

				<TextContent>
					<SetL />
					<Title>Arlo's Value</Title>
					<Text>
						With Arlo, patients steer their health journey alongside trusted
						doctors, never trading quality for cost. Up-front prices and rich
						preventive coverage create healthier people and healthier budgets.
					</Text>
				</TextContent>
			</TopSection>
			<AccordionWrapper>
				<AccordionComponent />
			</AccordionWrapper>
		</Wrapper>
	)
}

const Wrapper = styled("div", {
	...fresponsive(css`
		position: relative;
		display: flex;
		flex-direction: column;
		width: 100%;
		max-width: 1440px;
		margin: 0 auto;
		padding: 48px 32px 0;
	`),

	...ftablet(css`
		will-change: transform;
		max-width: 1024px;
		padding: 0 32px 32px;
	`),

	...fmobile(css`
		max-width: 375px;
		padding: 0 24px 32px;
	`),
})

const Carousel = styled(InfiniteSideScroll, {
	...fresponsive(css`
		width: 787px;
		height: 32px;
		border-left: none;
		align-items: center;
		position: absolute;
		bottom: 0;
		left: 0;

		.track {
			gap: 12px;
		}
	`),
	...ftablet(css`
		width: unset;
		height: 32px;
	`),

	...fmobile(css`
		width: 327px;
		border-left: 1px solid ${colors.green100};
		border-bottom: none;
	`),
})

const CarouselTitle = styled("h1", {
	...fresponsive(css`
		${textStyles.titleS};
		color: ${colors.orange};
		text-align: left;
		opacity: 0.4;
		margin-left: 32px;
		position: relative;
		z-index: 2;
		margin-bottom: 42px;
	`),

	...ftablet(css`
		margin-left: 8px;
	`),

	...fmobile(css`
		${textStyles.titleXS}
		margin-left: 8px;
	`),
})

const CarouselWrapper = styled("div", {
	...fresponsive(css`
		position: relative;
		z-index: 1;
		width: 100%;
	`),

	...ftablet(css`
		position: relative;
		display: flex;
		flex-direction: column;
		gap: 12px;
	`),
})

const TopSection = styled("div", {
	...fresponsive(css`
		width: 100%;
		display: grid;
		grid-template-columns: 787px auto;
		grid-gap: 0 42px;
		padding: 0 0 0 32px;
	`),

	...ftablet(css`
		grid-template-columns: 1fr 1fr;
		padding-top: 40px;
		padding-bottom: 0;
		direction: rtl;
		height: 463px;
		margin-top: -40px;
	`),

	...fmobile(css`
		padding: 0;
		display: flex;
		flex-direction: column-reverse;
		justify-content: space-between;
		gap: 62px;
	`),
})

const TextContent = styled("div", {
	...fresponsive(css`
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		align-items: flex-start;
		width: 100%;
		padding: 0 112px 32px 0;
	`),

	...ftablet(css`
		height: 433px;
		padding: 0;
		direction: ltr;
	`),

	...fmobile(css`
		padding: 0 77px 0 0;
		margin-bottom: 64px;
	`),
})

const WindowSection = styled("div", {
	...ftablet(css`
		height: 433px;
		padding: 0;
		direction: ltr;
	`),

	...fmobile(css`
		height: 145px;
	`),
})

const Window = styled("div", {
	...fresponsive(css`
		width: 787px;
		height: 378px;
		position: relative;
		display: flex;
		align-items: flex-end;
		justify-content: center;
		padding: 0 0 30px;
	`),

	...ftablet(css`
		width: unset;
		height: 378px;
		direction: ltr;
	`),

	...fmobile(css`
		width: 327px;
		height: 113px;
		padding-bottom: 16px;
		flex-shrink: 0;
	`),
})

const TopLayer = styled("div", {
	...fresponsive(css`
		display: block;
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		height: 50%;
		border: 1px solid ${colors.green300};
		border-top: none;
		border-radius: 0 0 16px 16px;
		z-index: 4;
	`),

	...ftablet(css`
		height: 10%;
	`),
})

const BottomLayer = styled("div", {
	...fresponsive(css`
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 50%;
		border: 1px solid ${colors.green300};
		border-bottom: none;
		border-radius: 16px 16px 0 0;
		z-index: 2;
	`),

	...ftablet(css`
		height: 90%;
	`),
})

const Woman = styled(StaticImage, {
	...fresponsive(css`
		position: absolute;
		display: inline;
		bottom: -30px;
		width: 380px;
		right: 135px;
		height: 457px;
		z-index: 3;
		transform: translateY(100%);
		max-block-size: unset !important;
		max-inline-size: unset !important;

		img {
			max-block-size: unset !important;
			max-inline-size: unset !important;
		}
	`),

	...ftablet(css`
		width: 392px;
		height: auto;
		right: -15px;
		bottom: -48px;
	`),

	...fmobile(css`
		position: absolute;
		width: 250px;
		height: auto;
		z-index: 4;
		right: -70px;
		bottom: -35px;
	`),
})

const AccordionWrapper = styled("div", {
	...fresponsive(css`
		width: 100%;
		height: 215px;
		background-color: ${colors.offWhite};
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 5;
	`),

	...ftablet(css`
		height: auto;
		max-width: 960px;
	`),

	...fmobile(css`
		height: auto;
		padding-bottom: 10px;
		max-width: 375px;
		margin-top: 1px;
	`),
})

const Title = styled("h1", {
	...fresponsive(css`
		${textStyles.h4};
		color: ${colors.black100};
		text-align: left;
		width: 100%;
		margin: 120px 0 28px;
		position: relative;
		z-index: 4;
	`),

	...fmobile(css`
		${textStyles.h6};
		color: ${colors.black100};
		text-align: left;
		width: 100%;
		margin: 24px 0;
	`),
})

const Text = styled("p", {
	...fresponsive(css`
		color: ${colors.green500};
		${textStyles.bodyR};
		text-align: left;
		width: 100%;
		margin-left: auto;
		margin-right: auto;
		display: block;
	`),

	...ftablet(css`
		height: 254px;
	`),

	...fmobile(css`
		${textStyles.bodyS};
		width: 240px;
		margin: 0;
	`),
})

const SetL = styled(SetLSVG, {
	...fresponsive(css`
		width: 50px;
		height: 50px;
		color: ${colors.green200};
	`),
})

const LeftBlur = styled("div", {
	...fresponsive(css`
		position: absolute;
		top: 0;
		left: 0;
		width: 40px;
		height: 100%;
		background: linear-gradient(
			270deg,
			rgba(251 250 246 / 0%) 0%,
			#fbfaf6 100%
		);
		z-index: 1;
	`),

	...fmobile(css`
		top: unset;
		bottom: -10px;
	`),
})

const RightBlur = styled(LeftBlur, {
	...fresponsive(css`
		position: absolute;
		left: unset;
		right: 0;
		transform: rotate(180deg);
		z-index: 1;
	`),
})

const AfterArlo = styled("span", {
	...fresponsive(css`
		opacity: 1;
		position: absolute;
		left: 0;
		color: ${colors.offWhite};
		z-index: 0;
	`),
})

const Before = styled("span", {
	...fresponsive(css`
		position: relative;
		opacity: 1;
		z-index: 1;
	`),
})
